import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BRAND_COLORS, UI_ASSETS, type UserRole } from '@lk/shared';
import { BrandImg } from '../../components/BrandImg';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { SyncIndicator } from '../../components/SyncIndicator';
import { humanMessage, isLoginFailed } from '../../lib/errors';
import { usePasskeyLogin, usePinLogin } from './useAuth';
import { useAuthGate } from './authGate';
import { ParentPasswordLogin } from './ParentPasswordLogin';
import { homeFor } from './RequireAuth';
import { PinPad } from './PinPad';
import { canLoginWithPasskey } from './webauthn';

/**
 * Вхід у застосунок — один екран, одне поле.
 *
 * Вибору профілю немає навмисно: дитина не має шукати себе в списку,
 * вона просто вводить свій PIN. Хто саме зайшов, вирішує сервер, тому
 * імена профілів не публікуються без автентифікації взагалі.
 */
export function LoginScreen({ expired = false }: { expired?: boolean }): React.JSX.Element {
  const navigate = useNavigate();
  const login = usePinLogin();
  const passkeyLogin = usePasskeyLogin();
  const clearGate = useAuthGate((s) => s.clear);
  const [errorNonce, setErrorNonce] = useState(0);
  /**
   * Кнопка зʼявляється тільки на пристрої, де ключ реально додавали, — байдуже
   * ким: батьком з кабінету чи дитиною з арени («Заходь пальцем»). На чужому
   * телефоні її немає, бо там натискати нічого: ключ живе в пристрої, а не
   * в акаунті, і сервер про його наявність підказати не може й не має права.
   *
   * Якщо на одному телефоні ключі і в батька, і в дитини — вибір робить сам
   * браузер: він показує список імен, і кожен натискає своє.
   */
  const [passkeyAvailable, setPasskeyAvailable] = useState(() => canLoginWithPasskey());

  useEffect(() => {
    if (!expired) clearGate();
  }, [expired, clearGate]);

  const goHome = (role: UserRole): void => {
    void navigate({ to: homeFor(role) });
  };

  /*
   * `pb-16` більший за верхній відступ навмисно. Вміст центрується по висоті,
   * тому нижній відступ ПІДНІМАЄ всю картку — а підняти її треба: на телефоні
   * нижня панель браузера зʼїдає смугу внизу, і остання кнопка («Face ID», а
   * під нею вхід для батьків) ховалася за неї. `lk-safe` рятує лише від
   * апаратної рамки (`safe-area-inset`), про панель браузера він не знає.
   */
  return (
    <main className="lk-shell lk-safe flex min-h-dvh flex-col justify-center gap-6 pt-8 pb-16">
      <header className="text-center">
        <BrandImg
          src={UI_ASSETS.logoMark.path}
          alt="LearnKids"
          fallbackIcon="star"
          colors={[BRAND_COLORS.gold, BRAND_COLORS.goldDeep]}
          shape="rounded"
          className="mx-auto h-20 w-20"
          eager
        />
        <h1 className="lk-title-gold mt-3 text-4xl">LearnKids</h1>
        <p className="mt-1 text-sm font-bold text-white/60">Арена знань. Відповідай — заробляй.</p>
      </header>

      {expired ? (
        <Panel tight className="border-[var(--lk-gold)]/50 p-4 text-center">
          <p className="text-sm font-bold">
            Сесія завершилась — зайди ще раз. Усі твої відповіді збережені, вони надішлються одразу
            після входу.
          </p>
          <SyncIndicator variant="compact" className="mt-3" />
        </Panel>
      ) : null}

      <Panel className="p-5">
        <PinPad
          disabled={login.isPending || passkeyLogin.isPending}
          errorNonce={errorNonce}
          onSubmit={(pin) => {
            passkeyLogin.reset();
            login.mutate(
              { pin },
              {
                onSuccess: (me) => goHome(me.user.role),
                onError: () => setErrorNonce((n) => n + 1),
              },
            );
          }}
        />

        {login.isError ? (
          <p className="mt-4 text-center text-sm font-bold text-[var(--lk-red)]" role="alert">
            {/*
              Невірний PIN і протухла сесія — різні події. Обидві приїжджають
              401-м, тому текст обирається за кодом: інакше на помилку в цифрі
              екран писав би «Потрібно зайти ще раз», і дитина не розуміла б,
              що просто натиснула не ту кнопку.
            */}
            {isLoginFailed(login.error) ? 'Невірний PIN. Спробуй ще раз.' : humanMessage(login.error)}
          </p>
        ) : null}

        {passkeyAvailable ? (
          <Button
            tone="ghost"
            block
            mute
            className="mt-4"
            loading={passkeyLogin.isPending}
            disabled={passkeyLogin.isPending || login.isPending}
            onClick={() =>
              passkeyLogin.mutate(undefined, {
                onSuccess: (me) => goHome(me.user.role),
                // Ключа на пристрої не знайшлося — кнопка йде з екрана,
                // лишається PIN. Повернути її можна в кабінеті батьків.
                onError: () => setPasskeyAvailable(canLoginWithPasskey()),
              })
            }
          >
            <Icon name="fingerprint" size={20} />
            Face ID / відбиток
          </Button>
        ) : null}

        {passkeyLogin.isError ? (
          <p className="mt-3 text-center text-sm font-bold text-[var(--lk-red)]" role="alert">
            {humanMessage(passkeyLogin.error)}
          </p>
        ) : null}

        <ParentPasswordLogin
          onSuccess={(me) => goHome(me.user.role)}
          onOpen={() => {
            login.reset();
            passkeyLogin.reset();
          }}
          disabled={login.isPending || passkeyLogin.isPending}
        />
      </Panel>
    </main>
  );
}
